import React from 'react'
import {useTranslation} from 'react-i18next'
import {TeamMember} from '../../domain/entities/Pipeline.ts'
import {ConversionCard as ConversionCardData, ConversionCta, isFinalConversionStage, isLostConversionStage} from '../../domain/entities/ConversionPipeline.ts'
import {cardTravelerName, dayCounter, formatMoney, memberById, nextActionLabel, relativeDue, TONE, useDateFormatters} from './pipelineView.ts'
import {Avatar, CardMenu, CardMenuItem, ChannelIcon, ConversionBadges, Countries, MarketChip, NextActionWho, Tag} from './PipelineParts.tsx'
import {ConversionCtaButtons} from './ConversionCtas.tsx'
import {RunConversionCta} from './useConversionCtaRunner.tsx'

type ConversionCardProps = {
    card: ConversionCardData
    team: TeamMember[]
    selected: boolean
    menuItems: CardMenuItem[]
    onOpen: (card: ConversionCardData) => void
    // Server-driven next steps under the card (they never open it).
    onRunCta: RunConversionCta
    isCtaRunning: (id: string, cta: ConversionCta) => boolean
    ctaBusy: boolean
}

const PAYMENT_TONE = {NONE: 'grey', LINK_CREATED: 'amber', PAID: 'green', FAILED: 'red'} as const

// Pages over the included ones, with the extra pages and their payment.
export const PagesLine: React.FC<{card: ConversionCardData}> = ({card}) => {
    const {t, i18n} = useTranslation('dashboard')
    const {uploaded, included, extra} = card.pages
    if (uploaded == null) return null
    const amount = formatMoney(card.payment.amountMinor, card.payment.currency, i18n.language)
    const showPayment = (extra ?? 0) > 0 || card.payment.status !== 'NONE'
    const ratio = included ? Math.min(1, uploaded / included) : 0
    return (
        <span className="block mt-3">
            <span className="flex items-center gap-2 min-w-0">
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4B5563" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="flex-none"><path d="M2 5.5C4.5 4.3 8 4.3 12 6c4-1.7 7.5-1.7 10-.5V19c-2.5-1.2-6-1.2-10 .5-4-1.7-7.5-1.7-10-.5z"/><path d="M12 6v13.5"/></svg>
                <span className="text-[12.5px] font-medium text-neutralBlack-800 whitespace-nowrap">
                    {included != null ? t('pipelines.pages.ofIncluded', {pages: uploaded, included}) : t('pipelines.pages.count', {count: uploaded})}
                </span>
                {(extra ?? 0) > 0 && <Tag tone="amber">{t('pipelines.pages.extra', {count: extra!})}</Tag>}
            </span>
            {included != null && included > 0 && (extra ?? 0) === 0 && (
                <span aria-hidden="true" className="block mt-1.5 h-1 rounded-sm bg-neutralBlack-100 overflow-hidden">
                    <span className="block h-full bg-[#14343B]" style={{width: `${Math.round(ratio * 100)}%`}}/>
                </span>
            )}
            {showPayment && (
                <span className="block mt-2">
                    <Tag tone={PAYMENT_TONE[card.payment.status] ?? 'grey'}>
                        {t(`pipelines.payment.${card.payment.status}`, {amount: amount ?? '—'})}
                    </Tag>
                </span>
            )}
        </span>
    )
}

export const ConversionCard: React.FC<ConversionCardProps> = ({card, team, selected, menuItems, onOpen, onRunCta, isCtaRunning, ctaBusy}) => {
    const {t} = useTranslation('dashboard')
    const {formatTripRange} = useDateFormatters()
    const name = cardTravelerName(card, t)
    const isFinal = isFinalConversionStage(card.crmStage)
    const counter = dayCounter(card.trip, t)
    const editor = memberById(team, card.editorId)
    const due = card.nextAction ? relativeDue(card.nextAction.dueAt, t) : null
    const isWait = card.nextAction?.type === 'WAIT'
    const agencyLabel = card.agencyName ?? card.agencyId ?? t(`pipelines.salesChannel.${card.channel ?? 'B2C'}`)

    return (
        <div
            className={`group relative flex-none rounded-xl border bg-white transition-colors duration-200 ease-out ${
                selected ? 'border-neutralBlack-900' : 'border-neutralBlack-200 hover:border-neutralBlack-400'
            }`}
        >
            <button type="button" onClick={() => onOpen(card)}
                    aria-label={t('pipelines.card.open', {name})}
                    className="block w-full text-left p-3.5 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutralBlack-900">
                <span className="flex items-center gap-1.5 pr-7">
                    <Countries countries={card.trip.countries}/>
                    <span className="ml-0.5 text-[15px] font-bold tracking-[-.01em] text-neutralBlack-900 whitespace-nowrap">
                        {formatTripRange(card.trip.startDate, card.trip.endDate)}
                    </span>
                </span>
                <span className="block mt-2.5 text-[14px] font-medium text-neutralBlack-900 truncate">{name}</span>
                <span className="flex items-center gap-2 mt-1.5 min-w-0">
                    <Avatar name={agencyLabel} size={22}/>
                    <span className="text-[12px] text-neutralBlack-600 truncate min-w-0">{agencyLabel}</span>
                    {card.editorId && (
                        <span className="ml-auto flex-none inline-flex items-center gap-1 h-6 pl-0.5 pr-2 rounded-full bg-neutralBlack-100"
                              title={t('pipelines.card.editor', {name: editor?.name ?? card.editorId})}>
                            <span className="sr-only">{t('pipelines.card.editor', {name: editor?.name ?? card.editorId})}</span>
                            <Avatar member={editor} name={editor?.name ?? card.editorId} size={20}/>
                            <span aria-hidden="true" className="text-[11px] font-semibold text-neutralBlack-700">{t('pipelines.card.editorShort')}</span>
                        </span>
                    )}
                </span>

                <span className="flex items-center flex-wrap gap-1.5 mt-3">
                    {card.mode && (
                        <span className={`inline-flex items-center h-5 px-2 rounded-md text-[11px] font-semibold ${TONE.teal}`}>{t(`pipelines.mode.${card.mode}`)}</span>
                    )}
                    {!isFinal && <ChannelIcon channel={card.traveler.preferredChannel}/>}
                    <MarketChip market={card.market}/>
                    {counter && (
                        <span className="inline-flex items-center h-5 px-2 rounded-md bg-neutralBlack-100 text-[11px] font-semibold text-neutralBlack-700">{counter}</span>
                    )}
                </span>

                {card.crmStage === 'DRAFT_READY' && card.draftVersion && (
                    <span className="block mt-2.5">
                        <Tag tone="teal">{t(`pipelines.draftVersion.${card.draftVersion}`)}</Tag>
                    </span>
                )}

                <PagesLine card={card}/>

                {(card.flags.onHold || card.flags.agencyReview || card.alerts.length > 0) && (
                    <span className="block mt-2">
                        <ConversionBadges onHold={card.flags.onHold} agencyReview={card.flags.agencyReview}
                                          alerts={card.alerts} messagesSent={card.messagesSent}/>
                    </span>
                )}

                {isLostConversionStage(card.crmStage) && (
                    <span className="block mt-2">
                        <Tag tone="red">
                            {card.lostReason ? t('pipelines.conversion.lostBadge', {reason: t(`pipelines.lostReason.${card.lostReason}`)}) : t('pipelines.stage.LOST')}
                        </Tag>
                    </span>
                )}

                {card.nextAction && (
                    <span className="flex items-center gap-2 mt-3 pt-2.5 border-t border-neutralBlack-100 min-w-0 whitespace-nowrap">
                        <NextActionWho nextAction={card.nextAction} team={team} systemWhenWaiting={!isFinal && !card.ctas?.length}/>
                        <span className={`text-[12px] truncate min-w-0 ${isWait ? 'text-[#8A5A00] font-medium' : 'text-neutralBlack-700'}`}>
                            {nextActionLabel(card.nextAction, t)}
                        </span>
                        {due && (
                            <span className={`ml-auto flex-none text-[11.5px] ${due.overdue ? 'text-[#B42318] font-semibold' : 'text-neutralBlack-600 font-medium'}`}>
                                {due.text}
                            </span>
                        )}
                    </span>
                )}
            </button>

            {(card.ctas?.length ?? 0) > 0 && (
                <div className="px-3.5 pb-3 -mt-1">
                    <ConversionCtaButtons conversionId={card.id} ctas={card.ctas} variant="compact"
                                          isRunning={isCtaRunning} disabled={ctaBusy} onRun={onRunCta}/>
                </div>
            )}

            <CardMenu label={t('pipelines.card.actions', {name})} items={menuItems}/>
        </div>
    )
}
